import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  CheckCircle2,
  Gift,
  Calendar,
  XCircle,
  DollarSign,
} from "lucide-react";
import { motion } from "framer-motion";
import SectionHeader from "@/components/site/SectionHeader";
import ServiceCard from "@/components/site/ServiceCard";
import PackageCard from "@/components/site/PackageCard";
import Hero3D from "@/components/site/Hero3D";
import SectionDivider from "@/components/site/SectionDivider";
import { Reveal } from "@/components/site/Reveal";
import Seo from "@/components/seo/Seo";
import {
  faqSchema,
  offerCatalogSchema,
  freeProgramOfferSchema,
  serviceSchema,
  breadcrumbSchema,
} from "@/components/seo/schema";
import { SERVICES } from "@/lib/services";
import { PACKAGE_GROUPS, HOSTING_PLANS } from "@/lib/packages";
import {
  FREE_PROGRAM,
  TRUST_INDICATORS,
  HOW_IT_WORKS,
  PROBLEM,
  FREE_FAQS,
  COMPARISON,
} from "@/lib/freeProgram";

const STATS = [
  { value: "$0", label: "Upfront website build cost" },
  { value: "6 mo", label: "Minimum commitment" },
  { value: "100%", label: "Custom-engineered builds" },
  { value: "Included", label: "Hosting, maintenance & support" },
];

export default function Home() {
  const webPackages = PACKAGE_GROUPS.find((g) => g.id === "web").packages;
  return (
    <div data-testid="page-home">
      <Seo
        title="Premium Custom Websites Built For Free | Equinox Systems"
        description="Equinox Systems builds premium custom websites with no upfront website development cost. Veteran-owned, nationwide remote web development. Apply for a free website today."
        path="/"
        schemas={[
          freeProgramOfferSchema(),
          serviceSchema(
            "Free Custom Website Program",
            "Premium custom website with $0 upfront build cost, hosting and maintenance at $200/month, 6-month minimum, ownership transfer included.",
            "/"
          ),
          offerCatalogSchema(),
          faqSchema(FREE_FAQS),
          breadcrumbSchema([{ name: "Home", path: "/" }]),
        ]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="glow-orb w-[520px] h-[520px] bg-blue-700/20 -top-40 -left-32 animate-pulse-glow" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 lg:pt-20 pb-24 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <motion.div
              className="lg:col-span-6"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="eq-chip">
                <Sparkles className="w-3 h-3" /> Veteran-Owned · Enterprise-Grade Engineering
              </div>
              <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-[4rem] font-bold tracking-tight leading-[1.05]">
                Premium Custom Websites{" "}
                <span className="eq-gradient-text text-glow">Built For Free</span>
              </h1>
              <p className="mt-6 text-lg text-gray-400 max-w-xl leading-relaxed">
                Equinox Systems designs and engineers high-performance websites,
                applications, CRMs, and AI-powered automations, built for companies
                ready to operate smarter and grow faster.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/apply" className="btn-primary" data-testid="hero-apply-btn">
                  Apply For A Free Website <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/packages" className="btn-ghost" data-testid="hero-view-packages-btn">
                  View Traditional Packages
                </Link>
                <Link to="/quote" className="btn-outline-blue" data-testid="hero-quote-btn">
                  Free Custom Quote
                </Link>
              </div>

              {/* Trust indicators */}
              <ul className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3" data-testid="hero-trust-indicators">
                {TRUST_INDICATORS.map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" strokeWidth={2} />
                    {t}
                  </li>
                ))}
              </ul>
            </motion.div>

            <div className="lg:col-span-6">
              <Hero3D />
            </div>
          </div>

          {/* STATS BAND */}
          <Reveal delay={0.1} className="mt-16 lg:mt-20">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {STATS.map((s) => (
                <div
                  key={s.label}
                  className="glass rounded-2xl p-6 text-center sm:text-left"
                  data-testid={`hero-stat-${s.label.split(" ")[0].toLowerCase()}`}
                >
                  <div className="font-display text-3xl font-bold eq-gradient-text">{s.value}</div>
                  <div className="mt-1 text-sm text-gray-400">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-white/5 py-6 overflow-hidden">
        <div className="flex gap-12 animate-marquee whitespace-nowrap text-sm text-gray-500 uppercase tracking-[0.22em]">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex gap-12">
              <span className="text-blue-300/80">Free Custom Websites</span><span className="text-blue-500/60">·</span>
              <span>Blue Collar Businesses</span><span className="text-blue-500/60">·</span>
              <span>Local Service Companies</span><span className="text-blue-500/60">·</span>
              <span>Small Businesses</span><span className="text-blue-500/60">·</span>
              <span>Startups</span><span className="text-blue-500/60">·</span>
              <span>Corporate Businesses</span><span className="text-blue-500/60">·</span>
            </div>
          ))}
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-24" id="problem">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeader eyebrow="The Problem" title={PROBLEM.title} description={PROBLEM.subheadline} align="center" />
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {PROBLEM.options.map((opt, i) => (
              <Reveal key={opt.tag} delay={i * 0.08}>
                <div className="eq-card rounded-2xl p-8 h-full" data-testid={`problem-option-${i + 1}`}>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-red-300/80">
                    <XCircle className="w-4 h-4" /> {opt.tag}
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-semibold">{opt.title}</h3>
                  <p className="mt-3 text-gray-400 leading-relaxed">{opt.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 glass rounded-2xl p-8">
              <p className="text-gray-300">
                Most business websites fail to{" "}
                {PROBLEM.failures.map((f, i) => (
                  <span key={f}>
                    <span className="text-white font-medium">{f.toLowerCase()}</span>
                    {i < PROBLEM.failures.length - 1 ? ", " : ". "}
                  </span>
                ))}
                Equinox Systems is the better alternative — a premium, custom-built website with
                zero upfront development cost, engineered to convert.
              </p>
              <Link to="/apply" className="btn-primary mt-6" data-testid="problem-apply-btn">
                Apply For A Free Website <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FREE WEBSITE PROGRAM */}
      <SectionDivider />
      <section className="py-24 relative" id="free-program">
        <div className="eq-aurora opacity-60" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeader
              eyebrow="Flagship Offer"
              title="Free Custom Website Program"
              description="A premium custom website with no upfront build cost — you only pay for hosting, maintenance, and support."
              align="center"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl glass-strong overflow-hidden grid lg:grid-cols-5">
              {/* Pricing column */}
              <div className="lg:col-span-2 p-10 border-b lg:border-b-0 lg:border-r border-white/10 relative">
                <div className="glow-orb w-72 h-72 bg-blue-600/25 -top-20 -left-10 animate-pulse-glow" />
                <div className="relative">
                  <div className="text-xs uppercase tracking-widest text-blue-400">Website Build</div>
                  <div className="font-display text-6xl font-bold eq-gradient-text mt-1">{FREE_PROGRAM.build}</div>
                  <div className="mt-6 space-y-3 text-sm">
                    <div className="flex items-center justify-between border-b border-white/10 pb-3">
                      <span className="text-gray-400">Hosting &amp; Maintenance</span>
                      <span className="text-white font-semibold">{FREE_PROGRAM.monthly}{FREE_PROGRAM.monthlySuffix}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-white/10 pb-3">
                      <span className="text-gray-400">Minimum Commitment</span>
                      <span className="text-white font-semibold">{FREE_PROGRAM.commitment}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Ownership Transfer</span>
                      <span className="text-white font-semibold">{FREE_PROGRAM.ownership}</span>
                    </div>
                  </div>
                  <Link to="/apply" className="btn-primary w-full mt-8" data-testid="free-program-apply-btn">
                    Apply Now <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              {/* Features column */}
              <div className="lg:col-span-3 p-10">
                <div className="text-xs uppercase tracking-widest text-blue-400 mb-5">What's Included</div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3.5">
                  {FREE_PROGRAM.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-gray-200">
                      <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24" id="how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeader
              eyebrow="How It Works"
              title="From application to ownership"
              description="A simple, transparent path — apply today and own your website after the initial term."
            />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {HOW_IT_WORKS.map((s, i) => (
              <Reveal key={s.step} delay={(i % 5) * 0.06}>
                <div className="eq-card rounded-2xl p-7 h-full" data-testid={`how-step-${s.step}`}>
                  <div className="font-display text-5xl font-bold eq-gradient-text">
                    {String(s.step).padStart(2, "0")}
                  </div>
                  <div className="mt-3 font-display text-lg">{s.title}</div>
                  <p className="mt-2 text-sm text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <SectionDivider />
      <section className="py-24" id="services">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeader
              eyebrow="Capabilities"
              title="A full engineering team, without the overhead"
              description="Six focused practice areas — architected to work together and scale as your business grows."
            />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <ServiceCard key={s.title} {...s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* PRICING COMPARISON TEASER */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeader
              eyebrow="Two Ways To Get Started"
              title="Free Program vs. Traditional Purchase"
              description="Choose the no-upfront-cost Free Program, or buy your website outright with immediate ownership."
              align="center"
            />
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {[COMPARISON.free, COMPARISON.traditional].map((opt, i) => (
              <Reveal key={opt.name} delay={i * 0.08}>
                <div
                  className={`rounded-3xl p-8 h-full border ${
                    i === 0
                      ? "border-blue-500/40 bg-gradient-to-br from-blue-500/10 to-transparent"
                      : "border-white/10 bg-white/[0.03]"
                  }`}
                  data-testid={`compare-${i === 0 ? "free" : "traditional"}`}
                >
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-400">
                    {i === 0 ? <Sparkles className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
                    {i === 0 ? "Option A" : "Option B"}
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-semibold">{opt.name}</h3>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-display text-4xl font-bold eq-gradient-text">{opt.price}</span>
                    <span className="text-sm text-gray-400">{opt.priceNote}</span>
                  </div>
                  <div className="mt-1 text-sm text-gray-400">{opt.monthly} · {opt.commitment}</div>
                  <Link
                    to={i === 0 ? "/apply" : "/packages"}
                    className={`${i === 0 ? "btn-primary" : "btn-ghost"} w-full mt-6`}
                    data-testid={`compare-cta-${i === 0 ? "free" : "traditional"}`}
                  >
                    {i === 0 ? "Apply For A Free Website" : "View Traditional Packages"}{" "}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/packages" className="btn-outline-blue" data-testid="home-full-comparison-btn">
              See Full Side-by-Side Comparison <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* WEB PACKAGES preview */}
      <section className="py-20 relative" id="packages">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeader
              eyebrow="Traditional Packages"
              title="Prefer to own your website immediately?"
              description="Buy your website outright with a one-time build and optional hosting — full ownership from day one."
            />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {webPackages.map((p) => <PackageCard key={p.id} pkg={p} />)}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/packages" className="btn-ghost" data-testid="home-view-all-packages-btn">
              View All Packages <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CUSTOM SOLUTION BANNER */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden glass-strong p-10 md:p-14">
              <div className="glow-orb w-96 h-96 bg-blue-600/25 -top-32 -right-32 animate-pulse-glow" />
              <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                <div className="max-w-2xl">
                  <div className="text-xs uppercase tracking-widest text-blue-400 mb-3">Custom Build</div>
                  <h3 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                    Building something ambitious?
                  </h3>
                  <p className="mt-3 text-gray-400 leading-relaxed">
                    From bespoke web applications and CRMs to AI automations, API integrations, and
                    enterprise platforms — we scope, design, and engineer the right solution around your goals.
                  </p>
                </div>
                <Link to="/quote" className="btn-primary shrink-0" data-testid="home-custom-quote-btn">
                  Receive a Custom Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DISCOUNT BANNER */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="rounded-3xl p-10 md:p-12 border border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-transparent backdrop-blur-xl flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/15 border border-blue-500/40 grid place-items-center text-blue-300 shadow-[0_0_24px_rgba(10,132,255,0.35)]">
                  <Gift className="w-6 h-6" strokeWidth={1.7} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-blue-400">Discount</div>
                  <div className="font-display text-2xl md:text-3xl font-semibold mt-1">15% Service Discount</div>
                  <p className="text-sm text-gray-400 mt-2 max-w-xl">
                    Proudly offered to military, veterans, first responders, teachers, and healthcare
                    workers. Verification may be required.
                  </p>
                </div>
              </div>
              <Link to="/quote" className="btn-outline-blue shrink-0" data-testid="discount-cta-btn">
                Claim Discount in Quote
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOSTING PREVIEW */}
      <SectionDivider />
      <section className="py-24" id="hosting">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeader
              eyebrow="Hosting & Maintenance"
              title="Fast, secure hosting from $29/month"
              description="Host with us and unlock discounted maintenance rates based on your hosting plan — with Premium Hosting including maintenance at no additional cost, plus daily backups, SSL, and 24/7 monitoring."
            />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOSTING_PLANS.map((p) => <PackageCard key={p.id} pkg={p} />)}
          </div>
        </div>
      </section>

      {/* REFERRAL */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="rounded-3xl border border-white/10 overflow-hidden grid md:grid-cols-2 glass">
              <div className="p-10 md:p-14">
                <div className="text-xs uppercase tracking-widest text-blue-400">Referral Program</div>
                <h3 className="font-display text-3xl md:text-4xl font-semibold mt-3 tracking-tight">
                  Refer a business, get rewarded
                </h3>
                <p className="text-gray-400 mt-4 leading-relaxed">
                  When you refer a new client to Equinox Systems, you both benefit — with no limit on
                  the free hosting months you can earn.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-gray-300">
                  <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> New client: 10% off project + 1 month free hosting</li>
                  <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> Referrer: 1 month free hosting after first payment</li>
                  <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" /> Unlimited referral rewards — keep them coming</li>
                </ul>
              </div>
              <div className="relative min-h-[260px]">
                <img
                  src="https://images.unsplash.com/photo-1745847768380-2caeadbb3b71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHBhcnRuZXJzaGlwfGVufDB8fHx8MTc4MDQzMjY1NXww&ixlib=rb-4.1.0&q=85"
                  alt="Business partnership handshake"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#05050a]/40 to-[#05050a]" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL APPLY CTA */}
      <SectionDivider />
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <div className="eq-chip mx-auto">
              <Sparkles className="w-3 h-3" /> Free Custom Website Program
            </div>
            <h3 className="mt-6 font-display text-4xl md:text-5xl font-semibold tracking-tight">
              Ready for a premium website at no upfront cost?
            </h3>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Apply for your free custom website today. We'll design and build it, launch it with
              hosting and maintenance included, and transfer ownership to you after six months.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/apply" className="btn-primary" data-testid="home-final-apply-btn">
                Apply For A Free Website <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/consultation" className="btn-ghost" data-testid="home-consultation-cta-btn">
                <Calendar className="w-4 h-4" /> Book Free Consultation
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
